import { type FormEvent, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getMyPractices, inviteStaff, listStaff, revokeStaffMember, updatePracticeVatRate } from '../../api/staff'
import { Button, ButtonLink } from '../../components/ui/Button'
import { Card, ErrorBox, SectionTitle } from '../../components/ui/Card'
import { inputClass, selectClass } from '../../components/ui/FormField'
import { useConfirm } from '../../components/ui/ConfirmModal'
import { useToast } from '../../components/ui/Toast'
import { useFetch } from '../../hooks/useFetch'
import { thClass, tdClass, tbodyHoverClass } from '@/lib/tableStyles'
import type { ClinicalRole, StaffMember } from '../../types/clinical'

const ROLE_LABELS: Record<ClinicalRole, string> = {
  veterinarian: 'Veterinarian',
  vet_technician: 'Vet Technician',
  receptionist: 'Receptionist',
  non_clinical: 'Non-Clinical',
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  active: { bg: 'var(--success-bg)', color: 'var(--success-text)' },
  invited: { bg: 'var(--warning-bg)', color: 'var(--warning-text)' },
  revoked: { bg: 'var(--danger-bg)', color: 'var(--danger-text)' },
  expired: { bg: 'var(--surface-alt)', color: 'var(--text-3)' },
}

export default function StaffList() {
  const { id: practiceIdStr } = useParams<{ id: string }>()
  const practiceId = Number(practiceIdStr)
  const toast = useToast()
  const confirm = useConfirm()

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteClinicalRole, setInviteClinicalRole] = useState<ClinicalRole>('veterinarian')
  const [inviteIsPracticeAdmin, setInviteIsPracticeAdmin] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [inviteError, setInviteError] = useState<string | null>(null)

  const [vatRate, setVatRate] = useState('15')
  const [savingVatRate, setSavingVatRate] = useState(false)

  const { data, loading, error, refetch } = useFetch(
    () => listStaff(practiceId),
    [practiceId],
  )

  const members: StaffMember[] = data ?? []

  // Prefill the current VAT rate from the caller's own practice membership, if available.
  useEffect(() => {
    getMyPractices()
      .then(ps => {
        const p = ps.find(x => x.id === practiceId)
        if (p) setVatRate((parseFloat(p.default_vat_rate) * 100).toFixed(2))
      })
      .catch(() => {})
  }, [practiceId])

  const handleSaveVatRate = async () => {
    if (savingVatRate) return
    setSavingVatRate(true)
    try {
      await updatePracticeVatRate(practiceId, (parseFloat(vatRate) || 0) / 100)
      toast.success('VAT rate updated.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update VAT rate.')
    } finally {
      setSavingVatRate(false)
    }
  }

  const handleInvite = async (e: FormEvent) => {
    e.preventDefault()
    if (!inviteEmail.trim() || inviting) return
    setInviting(true)
    setInviteError(null)
    try {
      await inviteStaff(practiceId, {
        email: inviteEmail.trim(),
        clinical_role: inviteClinicalRole,
        is_practice_admin: inviteIsPracticeAdmin,
      })
      toast.success(`Invitation sent to ${inviteEmail}.`)
      setInviteEmail('')
      setInviteIsPracticeAdmin(false)
      refetch()
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : 'Failed to send invitation.')
    } finally {
      setInviting(false)
    }
  }

  const handleRevoke = async (m: StaffMember) => {
    const ok = await confirm({
      title: 'Revoke access?',
      message: `${m.user_name ?? m.user_email} will no longer have access to this practice.`,
      confirmLabel: 'Revoke',
    })
    if (!ok) return
    try {
      await revokeStaffMember(practiceId, m.id)
      toast.success('Access revoked.')
      refetch()
    } catch {
      toast.error('Failed to revoke access.')
    }
  }

  return (
    <div className="max-w-[900px]">
      <h1 className="m-0 mb-6 text-[1.4rem] font-bold text-text-heading">Practice Staff</h1>

      {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

      {/* Practice settings */}
      <Card className="mb-5">
        <SectionTitle>Practice Settings</SectionTitle>
        <div className="flex gap-3 items-end flex-wrap">
          <div className="min-w-[160px]">
            <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">
              VAT rate (%)
            </label>
            <input
              type="number"
              value={vatRate}
              onChange={(e) => setVatRate(e.target.value)}
              className={`${inputClass()} box-border`}
              min={0} max={100} step={0.01}
            />
          </div>
          <Button type="button" onClick={() => void handleSaveVatRate()} disabled={savingVatRate}>
            {savingVatRate ? 'Saving…' : 'Save'}
          </Button>
          <ButtonLink to={`/practices/${practiceId}/services`} variant="outline">
            Manage Service Prices →
          </ButtonLink>
        </div>
        <p className="mt-3 mb-0 text-[0.8rem] text-text-3">
          This VAT rate pre-fills the tax field when staff create new invoices for this practice.
        </p>
      </Card>

      {/* Invite form */}
      <Card className="mb-5">
        <SectionTitle>Invite Staff Member</SectionTitle>
        {inviteError && <ErrorBox className="mb-3">{inviteError}</ErrorBox>}
        <form onSubmit={(e) => void handleInvite(e)} className="flex gap-3 flex-wrap items-end">
          <div className="flex-[2] min-w-[220px]">
            <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">
              Email address
            </label>
            <input
              className={`${inputClass()} box-border`}
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="staff@example.com"
              required
            />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[0.8rem] font-semibold text-text-heading mb-[0.3rem]">
              Clinical role
            </label>
            <select
              value={inviteClinicalRole}
              onChange={(e) => setInviteClinicalRole(e.target.value as ClinicalRole)}
              className={`${selectClass()} box-border`}
            >
              {(Object.keys(ROLE_LABELS) as ClinicalRole[]).map(r => (
                <option key={r} value={r}>{ROLE_LABELS[r]}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 pb-[0.55rem]">
            <input
              id="invite-admin"
              type="checkbox"
              checked={inviteIsPracticeAdmin}
              onChange={(e) => setInviteIsPracticeAdmin(e.target.checked)}
              className="w-4 h-4 cursor-pointer"
            />
            <label htmlFor="invite-admin" className="text-[0.8rem] font-semibold text-text-heading cursor-pointer whitespace-nowrap">
              Practice admin
            </label>
          </div>
          <Button type="submit" disabled={inviting || !inviteEmail.trim()}>
            {inviting ? 'Sending…' : 'Send Invitation'}
          </Button>
        </form>
        <p className="mt-3 mb-0 text-[0.8rem] text-text-3">
          The invitee must already have a Vet VPMS account. They'll receive an email with a link to accept.
        </p>
      </Card>

      {/* Staff table */}
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr>
                <th className={thClass}>Name / Email</th>
                <th className={thClass}>Role</th>
                <th className={thClass}>Status</th>
                <th className={thClass}>Invited</th>
                <th className={thClass}>Accepted</th>
                <th className={`${thClass} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody className={tbodyHoverClass}>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className={tdClass}>
                        <div className="h-3.5 bg-[var(--skeleton-from)] rounded w-[70%]" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : members.length === 0 ? (
                <tr>
                  <td colSpan={6} className={`${tdClass} text-center py-10 text-text-3`}>
                    No staff members yet — invite someone above.
                  </td>
                </tr>
              ) : (
                members.map((m) => (
                  <tr key={m.id}>
                    <td className={tdClass}>
                      <div className="font-semibold">{m.user_name ?? '—'}</div>
                      <div className="text-[0.8rem] text-text-2">{m.user_email}</div>
                    </td>
                    <td className={tdClass}>
                      <div>{ROLE_LABELS[m.clinical_role]}</div>
                      {m.is_practice_admin && (
                        <div className="text-[0.75rem] text-text-2 mt-[0.1rem]">Practice Admin</div>
                      )}
                    </td>
                    <td className={tdClass}>
                      <span
                        className="inline-flex items-center px-2 py-[0.15rem] rounded-full text-xs font-semibold"
                        style={{ background: (STATUS_COLORS[m.status] ?? STATUS_COLORS.expired).bg, color: (STATUS_COLORS[m.status] ?? STATUS_COLORS.expired).color }}
                      >
                        {m.status.charAt(0).toUpperCase() + m.status.slice(1)}
                      </span>
                    </td>
                    <td className={`${tdClass} text-text-2 text-[0.82rem]`}>
                      {m.invited_at ? new Date(m.invited_at).toLocaleDateString() : '—'}
                    </td>
                    <td className={`${tdClass} text-text-2 text-[0.82rem]`}>
                      {m.accepted_at ? new Date(m.accepted_at).toLocaleDateString() : '—'}
                    </td>
                    <td className={`${tdClass} text-right`}>
                      {m.status !== 'revoked' && (
                        <button
                          className="bg-transparent border-none cursor-pointer text-danger-text text-[0.82rem] font-medium py-[0.2rem] px-[0.4rem]"
                          onClick={() => void handleRevoke(m)}
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
